import { Component, OnInit, OnDestroy } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { Subject } from 'rxjs';
import { BaseComponent } from '../components/base.component';
import { MessageService } from 'primeng/api';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { SharedUserService } from '../services/shared-user.service';
import { CustomValidator } from '../custom-validator';
import { Constants } from '../constants';
import { UserResponseModel, PasswordExpiry } from '../models/response/user-response-model';
import { takeUntil, finalize } from 'rxjs/operators';
import { WebStorageService } from '../services/web-storage.service';
import { ActivatedRoute, Router } from '@angular/router';
import { ResultOfT } from 'src/app/shared/models/response/result';
import { FeatureFlagService } from '../services/feature-flag.service';
import { FeatureFlags } from '../feature-flags';
import { AuthenticationService } from '../services/authentication.service';

@Component({
  selector: 'app-change-password',
  templateUrl: './change-password.component.html',
  styleUrls: ['./change-password.component.sass'],
  standalone: false
})
export class ChangePasswordComponent extends BaseComponent implements OnInit, OnDestroy {
  private unsubscriber = new Subject<boolean>();
  title = 'Change Password';
  userId: number;
  email: string;
  isTherapist: boolean;
  isGoogleMap: boolean = false;
  formFields = {
    oldPassword: 'oldPassword',
    newPassword: 'newPassword',
    confirmPassword: 'confirmPassword'
  };
  value!: string;
  validationMessages = {
    oldPassword: {
      required: 'Current Password is required',
      minlength: 'Current Password should be at least 6 characters',
      invalid: 'Enter valid password'
    },
    newPassword: {
      required: 'New Password is required',
      minlength: 'New Password should be at least 6 characters',
      invalid: 'Enter valid password'
    },
    confirmPassword: {
      required: 'Confirm New Password is required',
      invalid: 'Enter valid confirm new password',
      minlength: 'Password should be at least 6 characters',
      validPasswordMatch: "Password doesn't match"
    }
  };
  passwordReminder = 'Reminder : Your password expires today. Update it now to maintain access.';
  reminder: number = -1;
  passwordExpiryNotification: boolean = false;
  passwordStrength: string = '';

  validationRules = [
    { key: 'validLength', message: 'At least 12 characters length.' },
    { key: 'validUpperCase', message: 'At least one uppercase letter.' },
    { key: 'validLowerCase', message: 'At least one lowercase letter.' },
    { key: 'validNumber', message: 'At least one number.' },
    { key: 'validSpecialChar', message: 'At least one special character.' }
  ];

  validUpperCase: boolean = false;
  validLowerCase: boolean = false;
  validNumber: boolean = false;
  validSpecialChar: boolean = false;
  validLength: boolean = false;
  passwordSecurity: boolean = false;

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private formBuilder: UntypedFormBuilder,
    private userService: SharedUserService,
    private titleService: Title,
    private webStorageService: WebStorageService,
    messageService: MessageService,
    private featureFlagService: FeatureFlagService,
    private authenticationService: AuthenticationService
  ) {
    super(messageService);
    this.buildForm();
    const webStore = this.webStorageService.getClientConfig();
    this.isGoogleMap = webStore.isGoogleMap;
  }

  async ngOnInit() {
    await this.checkPasswordFeature();
    this.checkPasswordSecurityFeature();
    const userToken = this.webStorageService.getCurrentUserToken();
    this.email = userToken.userEmail;
    this.userId = userToken.userId;
    this.isTherapist = userToken.roleName === Constants.userType.Therapist ? true : false;
    if (this.isTherapist) {
      this.titleService.setTitle('Appoint360');
    }
  }

  ngOnDestroy() {
    if (this.unsubscriber) {
      this.unsubscriber.next();
      this.unsubscriber.complete();
    }
  }

  checkPasswordSecurityFeature() {
    this.featureFlagService.getFeatureFlag(FeatureFlags.PasswordSecurity).subscribe((x) => {
      this.passwordSecurity = x.responseModel.isEnabled;

      if (this.passwordSecurity) {
        this.validationMessages = {
          oldPassword: {
            required: 'Current Password is required',
            minlength: 'Current Password should be at least 12 characters',
            invalid: 'Enter valid password'
          },
          newPassword: {
            required: 'Password is required',
            minlength: 'Password should be at least 12 characters',
            invalid: 'Enter valid password'
          },
          confirmPassword: {
            required: 'Password is required',
            invalid: 'Enter valid password',
            minlength: 'Password should be at least 12 characters',
            validPasswordMatch: "Password Doesn't match"
          }
        };
      }
      this.buildForm();
    });
  }

  async checkPasswordFeature() {
    await this.featureFlagService.getFeatureFlag(FeatureFlags.PasswordExpiryNotification).subscribe((x) => {
      this.passwordExpiryNotification = x.responseModel.isEnabled;
      if (this.passwordExpiryNotification) this.IsPasswordExpired();
    });
  }

  IsPasswordExpired() {
    const PwdExpiry = new PasswordExpiry();
    PwdExpiry.appUserEmail = this.email;
    this.userService
      .IsPasswordExpired(PwdExpiry)
      .pipe(
        takeUntil(this.unsubscriber),
        finalize(() => (this.loading = false))
      )
      .subscribe(
        (result: ResultOfT<number>) => {
          this.processResult<number>(result, () => {
            if (result.isSuccess) {
              if (result.responseModel != undefined && result.responseModel != null) {
                this.passwordReminder = 'Reminder : Your password expires in ' + result.responseModel + ' days. Update it now to maintain access.';
                this.reminder = result.responseModel;
                if (this.reminder == 0) this.passwordReminder = 'Reminder : Your password expires today. Update it now to maintain access.';
              }
            }
          });
        },
        (httpResponseError) => {
          this.showErrorMessage(httpResponseError.message);
        }
      );
  }

  onSubmit() {
    if (this.form.invalid) {
      this.markFormAsTouched();
      return;
    }
    this.loading = true;
    const userResponseModel = new UserResponseModel();
    userResponseModel.appUserEmail = this.email;
    userResponseModel.appUserInfoId = this.userId;
    userResponseModel.appUserpassword = this.form.get(this.formFields.confirmPassword).value;
    userResponseModel.appUserOldPassword = this.form.get(this.formFields.oldPassword).value;

    this.userService
      .changePassword(userResponseModel)
      .pipe(
        takeUntil(this.unsubscriber),
        finalize(() => (this.loading = false))
      )
      .subscribe((result) => {
        if (result.isSuccess) {
          this.loading = false;
          this.showSuccessMessage('Your password has been changed sucessfully');
          this.authenticationService.signOut();
          this.buildForm();
          setTimeout(() => {
            this.onCancel();
          }, 5000);
        } else {
          this.showErrorMessage(result.message);
        }
      });
  }
  onCancel() {
    this.buildForm();
    // add pagedRequestModel as state in navigation while redirecting to maintain state
    if (this.isTherapist) {
      this.router.navigate(['/therapist/my-appointments-new']);
    } else {
      this.router.navigate(['/admin/dashboard']);
    }
  }
  private buildForm() {
    const formGroup = this.formBuilder.group({});

    if (this.passwordSecurity) {
      formGroup.addControl(
        this.formFields.oldPassword,
        this.formBuilder.control('', {
          validators: [Validators.required, CustomValidator.whitespace],
          updateOn: Constants.formControlUpdateStrategy.blur
        })
      );

      formGroup.addControl(
        this.formFields.newPassword,
        this.formBuilder.control('', {
          validators: [Validators.required, CustomValidator.whitespace, this.passwordComplexityValidator()],
          updateOn: 'change'
        })
      );

      formGroup.addControl(
        this.formFields.confirmPassword,
        this.formBuilder.control('', {
          validators: [Validators.required, CustomValidator.whitespace],
          updateOn: 'change'
        })
      );
      formGroup.setValidators(CustomValidator.passwordMatch('newPassword', 'confirmPassword'));
      this.form = formGroup;
    } else {
      // tslint:disable-next-line: max-line-length
      formGroup.addControl(
        this.formFields.oldPassword,
        this.formBuilder.control('', { validators: [Validators.required, CustomValidator.whitespace], 
          updateOn: Constants.formControlUpdateStrategy.blur })
      );

      formGroup.addControl(
        this.formFields.newPassword,
        this.formBuilder.control('', {
          validators: [Validators.required, CustomValidator.whitespace, CustomValidator.passwordPattern],
          updateOn: Constants.formControlUpdateStrategy.blur
        })
      );
      // tslint:disable-next-line: max-line-length
      formGroup.addControl(
        this.formFields.confirmPassword,
        this.formBuilder.control('', {
          validators: [Validators.required, CustomValidator.whitespace, CustomValidator.passwordPattern],
          updateOn: Constants.formControlUpdateStrategy.blur
        })
      );
      formGroup.setValidators(CustomValidator.passwordMatch('newPassword', 'confirmPassword'));
      this.form = formGroup;
    }
  }

  passwordComplexityValidator() {
    return (control: any) => {
      const value = control.value || '';
      const hasUpperCase = /[A-Z]/.test(value);
      const hasLowerCase = /[a-z]/.test(value);
      const hasNumber = /\d/.test(value);
      const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(value);
      const isValidLength = value.length >= 12;

      this.validUpperCase = hasUpperCase;
      this.validLowerCase = hasLowerCase;
      this.validNumber = hasNumber;
      this.validSpecialChar = hasSpecialChar;
      this.validLength = isValidLength;

      if (hasUpperCase && hasLowerCase && hasNumber && hasSpecialChar && isValidLength) {
        return null;
      }

      return { passwordComplexity: true };
    };
  }

  passwordMatchValidator(group: UntypedFormGroup) {
    const newPassword = group.get('newPassword')?.value;
    const confirmPassword = group.get('confirmPassword')?.value;
    return newPassword === confirmPassword ? null : { validPasswordMatch: true };
  }

  onPasswordInput() {
    const password = this.form.get('newPassword')?.value || '';
    this.passwordStrength = this.calculatePasswordStrength(password);
  }

  calculatePasswordStrength(password: string): string {
    const lengthScore = password.length >= 12 ? 1 : 0;
    const complexityScore = [/[A-Z]/.test(password), /[a-z]/.test(password), /\d/.test(password), /[!@#$%^&*(),.?":{}|<>]/.test(password)].filter(Boolean).length;

    const totalScore = lengthScore + complexityScore;

    if (totalScore <= 2) return 'Weak';
    if (totalScore <= 4 && totalScore >= 2) return 'Medium';
    return 'Strong';
  }
}
